import React from "react";

//#region types
import { CommonEnums } from "models/enums";
import { DocumentFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { ClassifiedReceiptContainerProps } from "models/propTypes/receiptPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region services
import { useFileUploadService } from "serviceHooks";
import useDocumentDraftService from "serviceHooks/useDocumentDraftService";
//#endregion
//#region components
import { DocumentCreateForm, useCreateFormHelpers } from "components/documents";
import { FileHelper } from "utils/helpers";
import { OTHER_PAYMENT_METHOD_ID } from "utils/constants/receiptConstants";
//#endregion

const ClassifiedReceiptContainer: React.ForwardRefRenderFunction<FormHandles, ClassifiedReceiptContainerProps> = (
  { classifiedReceipt, onFormValidationChange },
  ref
) => {
  const documentDraftService = useDocumentDraftService();
  const fileUploadService = useFileUploadService();

  const {
    initialValues,
    validationSchema,
    onReceiptCategorySelect,
    onReceiptCategoryClear,
    onAmountChange,
    onCurrencyChange,
    availableDocumentTypes,
    receiptCategoriesInputItems,
    receiptPaymentMethods,
    topReceiptPaymentMethods,
    receiptPaymentMethodActions,
    dimensionAmount,
    currency,
    isReceiptLoading,
    setIsReceiptLoading,
  } = useCreateFormHelpers(
    false,
    true,
    classifiedReceipt.created,
    classifiedReceipt.images?.map((attachment) => ({
      ...attachment,
      fileName: attachment.fileName ?? FileHelper.getEmptyFileName(attachment.contentType ?? ""),
      fileSize: attachment.data?.length ?? 0,
      data: attachment.data ?? "",
      contentType: attachment.contentType ?? "",
    }))
  );

  const handleSubmit = async (values: DocumentFormTypes.CreateNew) => {
    const documentDraftPost: WintSuperkollAPIModel.DocumentDraftPost = {
      id: classifiedReceipt.id,
      type: documentDraftService.documentTypeToDocumentDraftType(CommonEnums.DocumentTypes.Receipt),
    };

    const files = await fileUploadService.updateMissingAttachments(
      values.filesToUpload ?? [],
      documentDraftService.getAttachmentData.bind(null, classifiedReceipt.id)
    );

    const paymentMethod = values.receipt.paymentMethod;

    const updatedValues: DocumentFormTypes.CreateNew = {
      ...values,
      filesToUpload: files,
      receipt: {
        ...values.receipt,
        paymentMethod: paymentMethod === OTHER_PAYMENT_METHOD_ID ? undefined : paymentMethod,
        isOtherPaymentMethod: paymentMethod === OTHER_PAYMENT_METHOD_ID,
      },
    };

    documentDraftPost.receipt = documentDraftService.formToReceiptPost(updatedValues);

    const res = await documentDraftService.post(documentDraftPost);

    return { type: values.type, res };
  };

  return (
    <DocumentCreateForm
      ref={ref}
      defaultValues={initialValues}
      validationSchema={validationSchema}
      handleSubmit={handleSubmit}
      receiptCategorySelectHandler={onReceiptCategorySelect}
      receiptCategoryClearHandler={onReceiptCategoryClear}
      amountChangeHandler={onAmountChange}
      currencyChangeHandler={onCurrencyChange}
      availableDocumentTypes={availableDocumentTypes}
      receiptCategories={receiptCategoriesInputItems}
      receiptPaymentMethods={receiptPaymentMethods}
      topReceiptPaymentMethods={topReceiptPaymentMethods}
      receiptPaymentMethodActions={receiptPaymentMethodActions}
      dimensionAmount={dimensionAmount}
      currency={currency}
      isReceiptLoading={isReceiptLoading}
      setIsReceiptLoading={setIsReceiptLoading}
      onFormValidationChange={onFormValidationChange}
      isClassifiedReceipt={true}
    />
  );
};

export default React.forwardRef(ClassifiedReceiptContainer);
